import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center space-x-[1rem] px-[2rem] h-[4.4rem] w-auto',
    'before:absolute before:top-0 before:left-0 before:w-full before:h-full',
    'before:transition-colors before:duration-500 before:ease-out-back',
    '[&>*]:relative [&>*]:inline-flex [&>*]:overflow-hidden',
    '[&>*>span]:transition-transform [&>*>span]:duration-300 [&>*>span]:ease-out-cubic',
    '[&>*>span]:hover:-translate-y-full',
    '[&>*]:after:content-[attr(data-label)] [&>*]:after:translate-y-full [&>*]:after:w-full [&>*]:after:text-current [&>*]:after:h-full [&>*]:after:text-small [&>*]:after:uppercase [&>*]:after:absolute [&>*]:after:top-0 [&>*]:after:left-0',
    '[&>*]:after:transition-transform [&>*]:after:duration-300 [&>*]:after:ease-out-cubic',
    '[&>*]:hover:after:translate-y-0 [&>*]:disabled:after:hidden'
  ],
  {
    variants: {
      variant: {
        light: 'text-white before:bg-dark-500 hover:before:bg-dark-900',
        dark: 'text-dark-500 before:bg-white hover:before:bg-dark-200'
      }
    },
    defaultVariants: {
      variant: 'light'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    startIcon?: ReactElement
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { children, variant, startIcon, className, asChild = false, ...props },
    ref
  ) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, className }))}
        {...props}
      >
        {startIcon}
        <Slottable>{children}</Slottable>
      </Component>
    )
  }
)

export default Button
